import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getStoredUser,
  isAuthenticated as checkAuth,
  type UserInfo,
} from '@/shared/lib/auth';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiAuthMeGet } from '@/entities/auth/modules/api/login';
import type {
  ApiAuthMeStaffPermissions,
  ApiAuthMeStaffResponse,
  ApiAuthMeStaffRole,
} from '@/shared/types/api/auth';

const AUTH_EVENT = 'jc-admin:auth-changed';

export const notifyAuthChanged = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(AUTH_EVENT));
};

type AuthState = {
  user: UserInfo | null;
  isAuthenticated: boolean;
};

const readState = (): AuthState => ({
  user: getStoredUser(),
  isAuthenticated: checkAuth(),
});

export type CurrentUserState = AuthState & {
  /**
   * Full payload returned by `/auth/me` for staff sessions (includes
   * `role` and `permissions`). `null` until first fetch resolves or when
   * not authenticated.
   */
  me: ApiAuthMeStaffResponse | null;
  /** Convenience getter — `me.role`, or null. */
  role: ApiAuthMeStaffRole | null;
  /** Convenience getter — `me.permissions`, or null. */
  permissions: ApiAuthMeStaffPermissions | null;
};

export const useCurrentUser = (): CurrentUserState => {
  const [state, setState] = useState<AuthState>(readState);

  useEffect(() => {
    const handler = (): void => setState(readState());
    window.addEventListener(AUTH_EVENT, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(AUTH_EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  // Pull rich profile (role + permissions) once authenticated, for staff only.
  // Don't gate any UI by these yet — just expose them. We tolerate failures
  // silently so legacy backends without /auth/me keep working.
  const meQuery = useQuery<ApiAuthMeStaffResponse, Error>({
    queryKey: queryKeys.auth.me,
    queryFn: apiAuthMeGet,
    enabled: state.isAuthenticated && state.user?.type === 'staff',
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const me = meQuery.data ?? null;
  return {
    ...state,
    me,
    role: me?.role ?? null,
    permissions: me?.permissions ?? null,
  };
};
